import React, { useMemo } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
  Box,
  TextField,
  Typography,
} from "@mui/material";
import { StyledMenuItem } from "../GroupedSelect";
import {
  styleForTextField,
  TextFieldForViewOnly,
} from "../TextField/TextField";

// Options: { value, label, reference?, details?, searchText?, disabled? }.
// onChange receives a value (or ""), or an array of values when multiple is true.
export const SearchableDropdown = ({
  multiple = false,
  value = multiple ? [] : "",
  options = [],
  onChange,
  onBlur,
  id,
  name,
  label,
  required = false,
  error = false,
  helperText,
  getOptionLabel,
  ariaLabel = label || "Select an option",
  placeholder = "Search options...",
  noOptionsText = "No matching options",
  canEdit = true,
  disabled = false,
  disableClearable = false,
  maxListHeight = 320,
  popperSx = {},
  actionLabel,
  onAction,
  getSelectionSummary,
  sx = {},
}) => {
  const selected = useMemo(
    () => multiple
      ? value
          .map((item) => options.find((option) => option.value === item))
          .filter(Boolean)
      : options.find((option) => option.value === value) || null,
    [multiple, value, options]
  );
  const selectionLabel = (option) =>
    getOptionLabel
      ? getOptionLabel(option)
      : [option.label, option.reference].filter(Boolean).join(" · ");

  if (!canEdit) {
    return (
      <TextFieldForViewOnly
        value={
          multiple
            ? selected.map(selectionLabel).join(", ") || "—"
            : selected
              ? selectionLabel(selected)
              : "—"
        }
        sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
      />
    );
  }

  return (
    <Autocomplete
      id={id}
      fullWidth
      size="small"
      openOnFocus
      selectOnFocus
      clearOnBlur
      multiple={multiple}
      disableCloseOnSelect={multiple}
      disabled={disabled}
      disableClearable={disableClearable}
      options={options}
      value={selected}
      getOptionLabel={(option) => option.actionLabel || selectionLabel(option)}
      getOptionKey={(option) => option.value}
      isOptionEqualToValue={(option, current) => option.value === current.value}
      getOptionDisabled={(option) => Boolean(option.disabled)}
      onChange={(_, option, _reason, details) => {
        const changedOption = multiple ? details?.option : option;
        if (changedOption?.isAction) {
          onAction?.();
          return;
        }
        onChange?.(
          multiple ? option.map((item) => item.value) : option?.value ?? ""
        );
      }}
      filterOptions={(items, { inputValue }) => {
        const terms = inputValue.trim().toLocaleLowerCase().split(/\s+/);
        const matches = items.filter((option) => {
          const text = [
            option.label,
            option.reference,
            option.details,
            option.searchText,
          ].filter(Boolean).join(" ").toLocaleLowerCase();
          return terms.every((term) => text.includes(term));
        });
        if (onAction && actionLabel && !disabled) {
          matches.push({
            value: "__searchable-dropdown-action__",
            actionLabel,
            isAction: true,
          });
        }
        return matches;
      }}
      noOptionsText={noOptionsText}
      clearText={multiple ? "Clear selections" : "Clear selection"}
      // Keep the popup in a portal so a scrollable dialog/table cannot clip it.
      disablePortal={false}
      componentsProps={{ popper: { sx: popperSx } }}
      ListboxProps={{
        sx: {
          maxHeight: `min(${maxListHeight}px, 40vh)`,
          overflowY: "auto",
          overscrollBehavior: "contain",
        },
      }}
      renderOption={(props, option, { selected: isSelected }) => {
        const { key, ...optionProps } = props;
        if (option.isAction) {
          return (
            <li key={key} {...optionProps}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 0.75,
                  color: "primary.main",
                }}
              >
                <AddCircleOutlineIcon fontSize="small" />
                <Typography variant="body2" fontWeight={600}>
                  {option.actionLabel}
                </Typography>
              </Box>
            </li>
          );
        }
        return (
          <li key={key} {...optionProps}>
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.5,
              }}
            >
              {multiple && (
                <Checkbox
                  size="small"
                  checked={isSelected}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ "aria-hidden": true }}
                  sx={{ p: 0.5, flexShrink: 0 }}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ flex: 1, fontWeight: 500, whiteSpace: "normal", overflowWrap: "anywhere" }}
                  >
                    {option.label}
                  </Typography>
                  {option.reference && (
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {option.reference}
                    </Typography>
                  )}
                </Box>
                {option.details && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.25, whiteSpace: "normal", overflowWrap: "anywhere" }}
                  >
                    {option.details}
                  </Typography>
                )}
              </Box>
            </Box>
          </li>
        );
      }}
      renderTags={
        multiple ? (items) => (
          <Typography
            variant="body2"
            noWrap
            sx={{ minWidth: 0, maxWidth: "65%", pl: 0.5, fontWeight: 500 }}
          >
            {getSelectionSummary
              ? getSelectionSummary(items)
              : items.length === 1
                ? selectionLabel(items[0])
                : `${items.length} selected`}
          </Typography>
        ) : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          onBlur={onBlur}
          placeholder={multiple && selected.length > 0 ? "" : placeholder}
          inputProps={{ ...params.inputProps, "aria-label": ariaLabel }}
        />
      )}
      sx={{
        bgcolor: "background.paper",
        ...(multiple ? {
          "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap" },
          "& .MuiAutocomplete-input": { minWidth: 30 },
        } : {}),
        ...sx,
      }}
    />
  );
};

// A searchable checkbox dropdown. value and onChange use arrays of option values.
// getSelectionSummary receives the selected option objects for the compact display.
export const SearchableMultiSelectDropdown = ({ value = [], ...props }) => (
  <SearchableDropdown {...props} multiple value={value} />
);

export const JustDropDown = ({
  label,
  value,
  options,
  onChange,
  maxWidth = 200,
  size = "medium",
  disabled,
  canEdit = true,
  onClose,
  onOpen,
  sx = {},
}) => {
  return (
    <FormControl sx={{ width: maxWidth }}>
      <InputLabel>{label}</InputLabel>
      {canEdit ? (
        <Select
          value={value}
          onChange={onChange}
          onClose={onClose}
          onOpen={onOpen}
          label={label}
          size={size}
          disabled={disabled}
          sx={{
            ...styleForTextField(),
            "& .MuiSelect-select": {
              fontWeight: 400,
            },
            ...sx,
          }}
        >
          {options.map((option) => (
            <StyledMenuItem key={option} value={option}>
              {option}
            </StyledMenuItem>
          ))}
        </Select>
      ) : (
        <TextFieldForViewOnly value={value} />
      )}
    </FormControl>
  );
};

export const DropDownByObj = ({
  label,
  value,
  options,
  onChange,
  maxWidth = 200,
  size = "medium",
  disabled,
  displayEmpty,
  formControlSx = {},
  renderValue,
  sx = { backgroundColor: "white" },
  variant,
}) => {
  return (
    <FormControl sx={{ width: maxWidth, ...formControlSx }}>
      {label ? <InputLabel>{label}</InputLabel> : null}
      <Select
        value={value}
        onChange={onChange}
        {...(label ? { label } : {})}
        size={size}
        disabled={disabled}
        displayEmpty={displayEmpty}
        renderValue={renderValue}
        variant={variant}
        sx={sx}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            sx={option.sx}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
